import { AbstractControl, FormArray, FormGroup, ValidationErrors } from '@angular/forms';

/**
 * Additional paths attached to a validation error by validators which validate
 * a serialized value (for example, the Bunker[] FormControl).
 */
export const INVALID_PATHS_ERROR_KEY = 'invalidPaths';
export const ROOT_FORM_PATH = 'form';

export interface InvalidControlPath {
  path: string;
  errorKeys: string[];
}

/**
 * Attaches paths without changing the existing validation error keys. The
 * paths are metadata only and never contain control values.
 */
export function withInvalidPaths(errors: ValidationErrors, paths: readonly string[]): ValidationErrors {
  const uniquePaths = [...new Set(paths.filter((path): path is string => typeof path === 'string'))];
  if (uniquePaths.length > 0) {
    Object.defineProperty(errors, INVALID_PATHS_ERROR_KEY, {
      configurable: true,
      // Angular composes async-validator results by spreading their error
      // objects, so this metadata must be enumerable to survive that merge.
      enumerable: true,
      value: uniquePaths,
      writable: false,
    });
  }
  return errors;
}

/**
 * Walks an Angular control tree and returns invalid paths in stable tree order.
 * FormGroup keys are sorted, FormArray indexes retain their numeric order.
 * Group/container errors are retained even when a child is also invalid.
 */
export function collectInvalidControlPaths(control: AbstractControl, path = ''): InvalidControlPath[] {
  return collectInvalidControlPathsWithState(control, path).paths;
}

interface InvalidControlPathCollection {
  paths: InvalidControlPath[];
  containsPending: boolean;
}

function collectInvalidControlPathsWithState(control: AbstractControl, path: string): InvalidControlPathCollection {
  const invalidPaths: InvalidControlPath[] = [];
  const controlPath = path || ROOT_FORM_PATH;
  const errors = control.errors;
  const errorKeys = Object.keys(errors || {})
    .filter((key) => key !== INVALID_PATHS_ERROR_KEY)
    .sort();
  const metadataPaths = getInvalidPathMetadata(errors);

  if (errors) {
    if (metadataPaths.length > 0) {
      metadataPaths.forEach((metadataPath) => {
        invalidPaths.push({
          path: joinControlPath(path, metadataPath),
          errorKeys,
        });
      });
    } else {
      invalidPaths.push({ path: controlPath, errorKeys });
    }
  }

  const childCollections: InvalidControlPathCollection[] = [];
  if (control instanceof FormGroup) {
    Object.keys(control.controls)
      .sort()
      .forEach((controlName) => {
        childCollections.push(
          collectInvalidControlPathsWithState(control.controls[controlName], joinControlPath(path, controlName)),
        );
      });
  } else if (control instanceof FormArray) {
    control.controls.forEach((childControl, index) => {
      childCollections.push(
        collectInvalidControlPathsWithState(childControl, joinControlPath(path, `[${index}]`)),
      );
    });
  }

  const hasPendingChild = childCollections.some(({ containsPending }) => containsPending);
  if (control.pending && !hasPendingChild) {
    const ownErrorPath = invalidPaths.find(({ path: invalidPath }) => invalidPath === controlPath);
    if (ownErrorPath) {
      ownErrorPath.errorKeys = [...new Set([...ownErrorPath.errorKeys, 'pending'])].sort();
    } else {
      invalidPaths.push({ path: controlPath, errorKeys: ['pending'] });
    }
  }

  childCollections.forEach(({ paths }) => invalidPaths.push(...paths));
  return {
    paths: deduplicateInvalidPaths(invalidPaths),
    containsPending: control.pending || hasPendingChild,
  };
}

/**
 * Convenience form for callers that only need the JSON/form attribute paths.
 */
export function getInvalidControlPaths(control: AbstractControl, path = ''): string[] {
  return collectInvalidControlPaths(control, path).map(({ path: controlPath }) => controlPath);
}

/**
 * Formats paths for a user-facing message or an error message. Error keys are
 * included in logs so group-level failures remain diagnosable; callers can
 * omit them from the toaster with includeErrorKeys=false.
 */
export function formatInvalidControlPaths(
  invalidPaths: readonly InvalidControlPath[],
  includeErrorKeys = true,
): string {
  return invalidPaths
    .map(({ path, errorKeys }) => {
      if (!includeErrorKeys || errorKeys.length === 0) {
        return path;
      }
      return `${path} (${errorKeys.join(', ')})`;
    })
    .join(', ');
}

export class InvalidFormError extends Error {
  constructor(readonly invalidPaths: readonly InvalidControlPath[]) {
    const formattedPaths = formatInvalidControlPaths(invalidPaths);
    super(`Form validation rejected: ${formattedPaths || ROOT_FORM_PATH}`);
    this.name = 'InvalidFormError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

function getInvalidPathMetadata(errors: ValidationErrors | null): string[] {
  const metadata = errors?.[INVALID_PATHS_ERROR_KEY];
  if (!Array.isArray(metadata)) {
    return [];
  }
  return metadata.filter((path): path is string => typeof path === 'string');
}

function joinControlPath(parentPath: string, childPath: string): string {
  if (!childPath) {
    return parentPath || ROOT_FORM_PATH;
  }
  if (!parentPath) {
    return childPath.startsWith('[') ? `${ROOT_FORM_PATH}${childPath}` : childPath;
  }
  if (childPath.startsWith('[')) {
    return `${parentPath}${childPath}`;
  }
  return `${parentPath}.${childPath}`;
}

function deduplicateInvalidPaths(invalidPaths: InvalidControlPath[]): InvalidControlPath[] {
  const seen = new Set<string>();
  return invalidPaths.filter(({ path, errorKeys }) => {
    const key = `${path}\u0000${errorKeys.join(',')}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}
